import { find, omit } from 'lodash-es'
import { getSnapshot, types } from 'mobx-state-tree'
import { isLength } from 'validator'

import { Contact, TContact } from '../Contacts/ContactsModel'
import { FormStringFieldModel } from '../FormField/FormFieldModel'
import { IFormValidation } from '../FormField/FormValidation'
import { LegalEntity, TLegalEntity } from '../LegalEntities/LegalEntitiesModel'
import { Location, TLocation } from '../Locations/LocationsModel'
import { AutocompleteResult } from '../NetworkAutocomplete/NetworkAutocompleteOption'

interface ICustomsAccountJSON {
  accountNumber?: string
  binNumber?: string
}

interface ICustomsSettingsJSON {
  customsDefermentAccount?: ICustomsAccountJSON
  vatAccount?: ICustomsAccountJSON
}

export interface IOrganizationResJSON {
  data: IOrganizationJSON[]
  total: number
}
export interface IOrganizationJSON {
  _id: string
  customerId: string
  isMain?: boolean
  name: string
  locationIds: string[]
  contactIds: string[]
  legalEntityIds: string[]
  customsSettings?: ICustomsSettingsJSON
  createdAt: string
  updatedAt: string
  deletedAt?: string
}

const OrganizationsExtras = types.model('OrganizationsExtra', {
  locations: types.array(Location),
  contacts: types.array(Contact),
  legalEntities: types.array(LegalEntity),
})

const CustomsAccount = types
  .model('CustomsAccount', {
    accountNumber: FormStringFieldModel,
    binNumber: FormStringFieldModel,
  })
  .views((self) => ({
    get isDirty(): boolean {
      return self.accountNumber.isDirty || self.binNumber.isDirty
    },
    get doesExist(): boolean {
      return self.accountNumber.value !== '' || self.binNumber.value !== ''
    },
  }))
  .actions((self) => ({
    submit() {
      self.accountNumber.isDirty = false
      self.binNumber.isDirty = false
    },
  }))

export const CustomsSettings = types
  .model('CustomsSettings', {
    customsDefermentAccount: types.optional(CustomsAccount, {}),
    vatAccount: types.optional(CustomsAccount, {}),
  })
  .preProcessSnapshot((snapshot) => ({
    customsDefermentAccount: snapshot.customsDefermentAccount ? snapshot.customsDefermentAccount : undefined,
    vatAccount: snapshot.vatAccount ? snapshot.vatAccount : undefined,
  }))
  .views((self) => ({
    get isDirty(): boolean {
      return self.customsDefermentAccount.isDirty || self.vatAccount.isDirty
    },
  }))
  .actions((self) => ({
    setDefermentAccountNumber(accountNumber: string): void {
      self.customsDefermentAccount.accountNumber.setValue(accountNumber)
    },
    setDefermentBinNumber(binNumber): void {
      self.customsDefermentAccount.binNumber.setValue(binNumber)
    },
    setVatAccountNumber(accountNumber: string): void {
      self.vatAccount.accountNumber.setValue(accountNumber)
    },
    setVatBinNumber(binNumber): void {
      self.vatAccount.binNumber.setValue(binNumber)
    },
    submit() {
      self.customsDefermentAccount.submit()
      self.vatAccount.submit()
    },
  }))

export type TCustomsSettings = typeof CustomsSettings.Type

export const Organization = types
  .model('Organization', {
    _id: types.maybe(types.identifier(types.string)),
    customerId: types.maybe(types.string),
    isMain: types.optional(types.boolean, false),
    name: FormStringFieldModel,
    locations: types.optional(types.array(types.reference(Location)), []),
    contacts: types.optional(types.array(types.reference(Contact)), []),
    legalEntities: types.optional(types.array(types.reference(LegalEntity)), []),
    customsSettings: types.optional(CustomsSettings, {}),
    createdAt: types.maybe(types.string),
    updatedAt: types.maybe(types.string),
    deletedAt: types.maybe(types.string),
  })
  .preProcessSnapshot((snapshot) => {
    const changeableFields = ['locationIds', 'contactIds', 'legalEntityIds']
    const preProcessed = omit(snapshot, changeableFields)
    return {
      ...preProcessed,
      locations: snapshot.locationIds,
      contacts: snapshot.contactIds,
      legalEntities: snapshot.legalEntityIds,
      customsSettings: snapshot.customsSettings ? snapshot.customsSettings : undefined,
    }
  })
  .actions((self) => ({
    setName(name: string): void {
      self.name.setValue(name)
    },
    setLocation(location: TLocation): void {
      self.locations.push(location)
    },
    setContact(contact: TContact): void {
      self.contacts.push(contact)
    },
    setLegalEntity(legalEntity: TLegalEntity): void {
      self.legalEntities.push(legalEntity)
    },
    removeLocation(location: TLocation): void {
      self.locations.remove(location)
    },
    removeContact(contactId: string): void {
      const contact = self.contacts.find((contact) => contact._id === contactId)
      // @ts-expect-error TS(2345) FIXME: Argument of type '({ _id: string | null; customerI... Remove this comment to see the full error message
      self.contacts.remove(contact)
    },
    removeLegalEntity(legalEntity: TLegalEntity): void {
      self.legalEntities.remove(legalEntity)
    },
    submit(): void {
      self.name.isDirty = true
    },
  }))
  .views((self) => ({
    validateName(): IFormValidation {
      return {
        isInvalid: self.name.isDirty && !isLength(self.name.value, { min: 1, max: 255 }),
        message: 'directory-validation_name',
      }
    },
  }))
  .views((self) => ({
    get isValid(): boolean {
      return !self.validateName().isInvalid
    },

    get organizationJSON(): Partial<IOrganizationJSON> {
      const snapshot = getSnapshot(self)
      const changeableFields = ['locations', 'contacts', 'legalEntities']
      if (!self.customsSettings.isDirty) {
        changeableFields.push('customsSettings')
      }
      const preProcessed = omit(snapshot, changeableFields)
      return {
        ...preProcessed,
        locationIds: snapshot.locations,
        contactIds: snapshot.contacts,
        legalEntityIds: snapshot.legalEntities,
      }
    },
    get autocompleteItem(): AutocompleteResult {
      // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      return { label: self.name.value, value: self._id, data: { entity: self } }
    },
  }))

export type TOrganization = typeof Organization.Type

export const OrganizationsModel = types
  .model('OrganizationsModel', {
    data: types.array(Organization),
    total: types.number,
    extras: types.optional(OrganizationsExtras, {
      locations: [],
      contacts: [],
      legalEntities: [],
    }),
  })
  .views((self) => ({
    get autocompleteItes(): AutocompleteResult[] {
      return self.data.map((organization) => organization.autocompleteItem)
    },
    getOrganization(organizationId: string): TOrganization {
      // @ts-expect-error TS(2322) FIXME: Type '({ _id: string | null; customerId: string | ... Remove this comment to see the full error message
      return find(self.data, (organization) => organization._id === organizationId)
    },
  }))

export type TOrganizationsModel = typeof OrganizationsModel.Type
